import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trimEnd } from 'lodash';
import type { IConfiguration } from '../../models/profile';
import { getAPIKey } from '../../services/login/login-service';
import {
  checkPromotionCode,
  checkReferralCode,
  saveMarketingPreferenceForUser,
  saveTermsAndCondition,
} from '../../services/login/loginApi-service';
import type { saveTermsConditionRequestData } from '../../models/login';
import { getProfile } from '../../services/user/user-service';
import type { AppDispatch } from '../../store';
import { logout, setCurrencyDetails } from '../../store/actions/app.Actions';
import { ActionType } from '../../store/actions/Helpers';
import type { IMarketingConsentState } from '../../types/stateTypes';
import { Common } from '../../utils/constants';

export const useReferralPromotionPreference = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const [initialValues, setInitialValues] = useState({
    referralCode: location?.state?.referralCode || '',
    promotionCode: '',
    isMarketingConsentApplied: null,
  });

  const [isValidPromotionCode, setIsValidPromotionCode] = useState<boolean | null>(null);
  const [isValidReferralCode, setIsValidReferralCode] = useState<boolean | null>(null);
  const [isReferralDisabled, setIsReferralDisabled] = useState<boolean>(false);

  const [isPackagePromotion, setIsPackagePromotion] = useState<boolean>(false);
  const [referralMessage, setReferralMessage] = useState<string>('');
  const [isReferralBonusOff, setIsReferralBonusOff] = useState<boolean>(false);
  const [isUsedPromotionCode, setIsUsedPromotionCode] = useState<boolean>(false);
  const [showMarketingConsentPopup, setShowMarketingConsentPopup] = useState<boolean>(false);

  const fetchData = async () => {
    const isReferralDisabledConfig = await getConfigData(Common.ConfigurationKey.DISABLE_REFERRAL);

    if (isReferralDisabledConfig.length > 0) {
      setIsReferralDisabled(isReferralDisabledConfig[0].value ? Boolean(isReferralDisabledConfig[0].value) : false);
    }
    const referralBonusOffConfig = await getConfigData(Common.ConfigurationKey.REFERRAL_BONUS);
    if (referralBonusOffConfig.length > 0) {
      const referralBonus = referralBonusOffConfig[0].value ? referralBonusOffConfig[0].value : '';

      if (referralBonus.trim().toLowerCase() === 'off') {
        setIsReferralBonusOff(true);
      } else {
        setIsReferralBonusOff(false);
      }
    }

    if (initialValues.referralCode) {
      checkReferralCodeValid(initialValues);
    }
  };

  const getConfigData = async (code: string) => {
    let configData: Array<IConfiguration> = [];
    await getAPIKey({ code })
      .then(async (res) => {
        configData = res.data;
      })
      .catch((m) => {
        toast.error(m.toString());
      });
    return configData;
  };

  const checkReferralCodeValid = async (values) => {
    if (!values.referralCode) {
      setIsValidReferralCode(null);
      return;
    }

    try {
      const response = await checkReferralCode(values.referralCode);
      if (response.Data) {
        setIsValidReferralCode(true);
      } else {
        setIsValidReferralCode(false);
      }
      setReferralMessage(response.Data.referral_message);

      if (
        values.promotionCode &&
        isValidReferralCode &&
        isValidPromotionCode &&
        isPackagePromotion &&
        isReferralDisabled
      ) {
        toast.error(t('termsAndConditionListing.errors.referralNotAllowed'));
        setInitialValues((prevValue) => ({
          ...prevValue,
          referralCode: '',
        }));
        setIsValidReferralCode(null);
      }
    } catch (error) {
      if (error.response?.data.message) {
        setIsValidReferralCode(false);
      } else {
        toast.error(error.toString());
      }
    }
  };

  const checkPromotionCodeValid = async (values) => {
    if (!values.promotionCode) {
      setIsValidPromotionCode(null);
      setIsUsedPromotionCode(false);
      return;
    }

    try {
      const response = await checkPromotionCode(values.promotionCode);
      if (response.Data) {
        setIsValidPromotionCode(true);
      } else {
        setIsValidPromotionCode(false);
      }
      setIsPackagePromotion(response.Data.is_package_promotion);

      if (
        values.referralCode &&
        isValidReferralCode &&
        isValidPromotionCode &&
        isPackagePromotion &&
        isReferralDisabled
      ) {
        toast.error(t('termsAndConditionListing.errors.referralNotAllowed'));
        setInitialValues((prevValue) => ({
          ...prevValue,
          referralCode: '',
        }));
        setIsValidPromotionCode(null);
      }
    } catch (error) {
      if (!error.response?.data?.message) {
        toast.error(error.toString());
        return;
      }

      const message = error.response.data.message;
      const isUsed = !message.includes(values.promotionCode);

      setIsValidPromotionCode(false);
      setIsUsedPromotionCode(isUsed);
      setReferralMessage('');
    }
  };

  const getProfileData = async () => {
    try {
      const userDetail = await getProfile();
      if (userDetail?.Data?.status?.code === Common.UserStatus.CANCELLED) {
        dispatch(logout()).then(() => {
          toast.error(t('login.toast.cancelled'), {
            autoClose: 15000,
          });
        });
        return;
      }

      dispatch(setCurrencyDetails(userDetail.Data?.default_currency));
      dispatch({
        type: ActionType.FETCH_USER_DETAILS,
        payload: userDetail.Data,
      });

      if (isValidPromotionCode) {
        toast.info(t('login.toast.reminderMessage'), {
          autoClose: 15000,
        });
      }

      if (!userDetail?.Data.is_profile_verified) {
        navigate(Common.Routes.PROFILE_REDIRECT);
        return;
      }

      if (userDetail?.Data.payment_type === null) {
        navigate(Common.Routes.PAYMENT);
        return;
      }

      navigate(Common.Routes.HOME);
    } catch {
      dispatch(logout());
      toast.error(t('login.toast.error'));
    }
  };

  const handleSubmit = async (values: IMarketingConsentState) => {
    if (isValidReferralCode !== null && !isValidReferralCode) {
      const message = () => {
        return (
          <>
            <span>
              {t('termsAndConditionListing.message.refferalCode.first')}{' '}
              <span className="themeTextColor">{values.referralCode}</span>{' '}
              {t('termsAndConditionListing.message.refferalCode.second')}
            </span>
          </>
        );
      };
      toast.error(message);
      return;
    }
    if (isValidPromotionCode !== null && !isValidPromotionCode) {
      const message = () => {
        return (
          <>
            <span>
              {t('termsAndConditionListing.message.promotionCode.first')}{' '}
              <span className="themeTextColor">{values.promotionCode}</span>{' '}
              {t('termsAndConditionListing.message.promotionCode.second')}
            </span>
          </>
        );
      };
      toast.error(message);
      return;
    }

    const input: saveTermsConditionRequestData = {
      terms_conditions: [],
    };
    if (isValidPromotionCode !== null && isValidPromotionCode) {
      Object.assign(input, {
        promotion_code: trimEnd(values.promotionCode),
      });
    }
    if (isValidReferralCode !== null && isValidReferralCode) {
      Object.assign(input, {
        referral_code: trimEnd(values.referralCode),
      });
    }

    try {
      await saveTermsAndCondition(input);
      if (referralMessage && isValidReferralCode) {
        toast.success(referralMessage);
      }
    } catch (error) {
      if (error.response?.message) {
        toast.error(error.response.message);
      } else {
        toast.error(error.toString());
      }
      return;
    }

    if (values.isMarketingConsentApplied === null) {
      setShowMarketingConsentPopup(true);
      return;
    }

    await saveMarketingPreference(values.isMarketingConsentApplied === 'true');
    await getProfileData();
    localStorage.removeItem(Common.SHOW_REFERRAL_SCREEN);
    localStorage.removeItem('referralCode');
  };

  const saveMarketingPreference = async (isMarketingConsentAccepted: boolean) => {
    try {
      const input = {
        is_marketing_consent_accepted: isMarketingConsentAccepted,
      };
      const response = await saveMarketingPreferenceForUser(input);
      if (response?.Data === true) {
        toast.success(response?.Messages);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.toString());
      }
    }
  };

  const savePreference = async (isMarketingConsentAccepted: boolean) => {
    await saveMarketingPreference(isMarketingConsentAccepted);
    await getProfileData();
    localStorage.removeItem(Common.SHOW_REFERRAL_SCREEN);
    localStorage.removeItem('referralCode');
  };

  const handleFocus = (event) => {
    if (event.target.name === 'promotionCode') {
      setIsValidPromotionCode(null);
    } else if (event.target.name === 'referralCode') {
      setIsValidReferralCode(null);
    }
  };

  return {
    fetchData,
    checkReferralCodeValid,
    handleSubmit,
    checkPromotionCodeValid,
    initialValues,
    isReferralDisabled,
    isPackagePromotion,
    referralMessage,
    isReferralBonusOff,
    showMarketingConsentPopup,
    isValidPromotionCode,
    isValidReferralCode,
    isUsedPromotionCode,
    setShowMarketingConsentPopup,
    setIsPackagePromotion,
    setIsReferralDisabled,
    setReferralMessage,
    getProfileData,
    savePreference,
    handleFocus,
  };
};
